import { Injectable } from '@angular/core'
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects'
import { catchError, map, switchMap, of, tap, filter } from 'rxjs'
import { caseActions } from './case.actions'
import { DatasetApiService } from '../../shared/services/dataset-api.service'
import { ToasterService } from '../../shared/services/toaster.service'
import { Store } from '@ngrx/store'
import { selectCaseId, selectHasCaseId } from './case.selectors'
import { RouterUtilsService } from '../../shared/services/router-utils.service'

@Injectable()
export class CaseEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly datasetApi: DatasetApiService,
        private readonly toaster: ToasterService,
        private readonly store: Store,
        private readonly routerUtils: RouterUtilsService
    ) {}

    selectCase$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(caseActions.selectCase),
                concatLatestFrom(() => this.store.select(selectHasCaseId)),
                switchMap(([{ caseId }, hasCaseId]) => {
                    return !hasCaseId
                        ? // User currently has no case open, go to case without updating viewer state
                          of(caseId)
                        : // User currently has case open, update viewer state before switching case
                          of(caseId).pipe(this.datasetApi.saveViewerState())
                }),
                tap(caseId => {
                    window.stop()
                    this.routerUtils.goToCase(caseId)
                })
            )
        },
        { dispatch: false }
    )

    reloadCase$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(caseActions.reloadCase),
                concatLatestFrom(() => this.store.select(selectCaseId)),
                filter(([, caseId]) => !!caseId),
                tap(([, caseId]) => {
                    this.routerUtils.goToCaseReload(caseId!)
                })
            )
        },
        { dispatch: false }
    )

    loadSegObjectPropertiesData$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadSegObjectPropertiesData),
            switchMap(({ caseId, segobjectpropertymap }) =>
                this.datasetApi.getSegObjectPropertiesData(caseId, segobjectpropertymap).pipe(
                    map(segobjectpropertiesdata =>
                        caseActions.loadingSegObjectPropertiesDataSucceeded({
                            segobjectpropertiesdata,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingSegObjectPropertyMapsFailed({
                                error,
                                message: 'Error loading seg object properties data.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadSegObjectPropertyMaps$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getSegObjectPropertyMaps(caseId).pipe(
                    map(segobjectpropertymaps =>
                        caseActions.loadingSegObjectPropertyMapsSucceeded({ segobjectpropertymaps })
                    ),
                    catchError(error => {
                        console.error('Error loading Seg Object Property Maps:', error) // Logs the error
                        return of(
                            caseActions.loadingSegObjectPropertyMapsFailed({
                                error,
                                message: 'Error loading segmentation property maps.',
                            })
                        )
                    })
                )
            )
        )
    })
    loadRecurrentNeighborhoodsResult$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getRecurrentNeighborhoodsResults(caseId).pipe(
                    map(rcn =>
                        caseActions.loadingRecurrentNeighborhoodsResultsSucceeded({
                            neighborhood: rcn,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingRecurrentNeighborhoodsResultsFailed({
                                error,
                                message: 'Error loading recurrent neighborhood.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadNeighborhoodEnrichmentResult$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getNeighborhoodEnrichmentResults(caseId).pipe(
                    map(neighborhoodEnrichment =>
                        caseActions.loadingNeighborhoodEnrichmentResultsSucceeded({
                            neighborhood: neighborhoodEnrichment,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingNeighborhoodEnrichmentResultsFailed({
                                error,
                                message: 'Error loading Neighborhood Enrichment.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadTsneResults$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getTsneResults(caseId).pipe(
                    map(tsne =>
                        caseActions.loadingTsneResultsSucceeded({
                            tsne: tsne,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingTsneResultsFailed({
                                error,
                                message: 'Error loading TSNE.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadUmapResults$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getUmapResults(caseId).pipe(
                    map(umap =>
                        caseActions.loadingUmapResultsSucceeded({
                            umap: umap,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingUmapResultsFailed({
                                error,
                                message: 'Error loading UMAP.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadAutoCellDetectionResult$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getAutoCellDetectionResults(caseId).pipe(
                    map(autoCellDetection =>
                        caseActions.loadingAutoCellDetectionResultsSucceeded({
                            autoCellDetection: autoCellDetection,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingAutoCellDetectionResultsFailed({
                                error,
                                message: 'Error loading Auto Cell Detection.',
                            })
                        )
                    )
                )
            )
        )
    })

    loadPlottingResult$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadCase),
            switchMap(({ caseId }) =>
                this.datasetApi.getPlottingResults(caseId).pipe(
                    map(plottingResult =>
                        caseActions.loadingPlottingResultsSucceeded({
                            plotting: plottingResult,
                        })
                    ),
                    catchError(error =>
                        of(
                            caseActions.loadingPlottingResultsFailed({
                                error,
                                message: 'Error loading Plotting.',
                            })
                        )
                    )
                )
            )
        )
    })

    showLoadingErrorToast$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    caseActions.loadingMarkersFailed,
                    caseActions.loadingRecurrentNeighborhoodsResultsFailed,
                    caseActions.loadingNeighborhoodEnrichmentResultsFailed,
                    caseActions.loadingAutoCellDetectionResultsFailed,
                    caseActions.loadingPlottingResultsFailed
                ),
                tap(({ error, message }) => {
                    this.toaster.httpError(error, message)
                })
            )
        },
        { dispatch: false }
    )
}
